// The pace of the table. The server is not a person: bots answer the engine synchronously, so the
// three cards that follow the player's own are decided, broadcast and delivered inside the same
// millisecond. A client that drew every message the moment it arrived would start four card
// flights at once and scoop the trick up on top of them, which is what a hand actually looked like
// before this file existed.
//
// So nothing is drawn straight from the socket. Every message is queued and shown one at a time,
// each followed by a pause long enough for what it did to be seen. Straight port of
// TrickGameFragment.pumpNotificationQueue and its siblings on Android, delays included, so a game
// played in a browser runs at the speed it runs on a phone.

import { Protocol } from '../generated/protocol.js';

// Every one of these is the Android constant of the same name: TrickGameFragment.DELAY_AFTER_*,
// MarzGameFragment's, VeciaGameFragment's, and the numbers HeartsGameFragment returns inline
export const Pace = Object.freeze({
  // Somebody else has been asked to play: their name is on the felt for a moment, and then the
  // card they chose arrives. For the local player there is nothing to pace - the whole table is
  // waiting for them, so nothing else can arrive until they answer
  OpponentTurnPrompt: 300,
  // Except when what they have been asked is a declaration - how many tricks in the Marz game, a
  // step of the auction in the chiamata. A bot decides those in the same millisecond too, and a
  // round of them landing one on top of the other reads as if nobody gave it a thought. So the
  // table waits longer there, a time drawn afresh each turn so the bots do not tick like a clock.
  // Not an Android constant: MarzGameFragment still waits its plain 300ms there. A person thinks
  // longer than this anyway, and their answer simply lands when the pause is over
  DeclarationThinkMin: 1000,
  DeclarationThinkMax: 2000,
  Bid: 450,
  PlayedCard: 600,
  TrickWinner: 1200,
  Call: 1400,
  // Tressette's accusi. Long, because it is the only moment those points are ever explained:
  // TressetteGameFragment.gameSpecificNotification holds the table for the same 2200ms
  Declarations: 2200,

  // La vecia
  Draw: 650,
  Pair: 1000,
  PlayerOut: 1100,

  // Hearts, where the pass is the whole plan for the hand and wants reading
  PassPrompt: 250,
  HeartsBroken: 900,
  NothingToPass: 1200,
  PassGivenAway: 1000,
  PassReceived: 2400,

  // The deal, whose length depends on the hand: TableView flies the cards in 90ms apart and each
  // flight lasts 260ms, so the last one lands after the whole stagger. The lead is Android's, and
  // here it is simply the beat of quiet the table gets once the hand is down
  DealLead: 350,
  DealStagger: 90,
  DealFlight: 260,
});

// Answering this instead of a delay stops the queue until the player says to go on. The end of a
// hand is the one moment the table has something to be read rather than watched, and on Android it
// is an overlay with a button on it; here it is the results panel and its "Continua"
export const HoldForPlayer = -1;

// How long the table should be left showing what this message did, before the next one is drawn.
// `state` is the table AFTER the message has been applied: how many cards a hand holds is what
// says how long its deal takes, and only the applied state knows that. `random` is Math.random
// unless a test wants to know the answer in advance
export function delayFor(item, state, random = Math.random) {
  const object = item.object;
  const tag = object?.[Protocol.JSONTagID];

  // A request addressed to a seat. The local player's own is not paced: the game stops for them
  if (item.kind === 'request') {
    return item.forMe ? 0 : promptDelayFor(tag, random);
  }

  switch (tag) {
    case Protocol.JSONStartMatchTag: {
      // Android reads its hand size off the setup message; here the hand itself is the answer,
      // which also covers la vecia, whose four cards Android paces with a flat number of its own
      const dealt = state?.myHand?.length ?? 0;
      return Pace.DealLead + Pace.DealStagger * dealt + Pace.DealFlight;
    }
    case Protocol.JSONPlayedValidCardTag:
      return Pace.PlayedCard;
    case Protocol.JSONWinnerTag:
      return Pace.TrickWinner;
    case Protocol.JSONBidResponseTag:
    case Protocol.JSONBetResponseTag:
      return Pace.Bid;
    case Protocol.JSONCallTag:
    case Protocol.JSONRedealTag:
      return Pace.Call;

    // Tressette: what everybody announced, worth points, and said once
    case Protocol.JSONDeclarationsTag:
      return Pace.Declarations;

    // La vecia. A deal here is four cards on a rubber table rather than a hand each, and the pause
    // that matters is the one after a draw: a pair being discarded is the whole point of the game
    case Protocol.JSONCardDrawnTag:
      return object[Protocol.JSONPairMadeID] ? Pace.Pair : Pace.Draw;
    case Protocol.JSONRemovedPlayerTag:
      return Pace.PlayerOut;

    // Hearts
    case Protocol.JSONHeartsBrokenTag:
      return Pace.HeartsBroken;
    case Protocol.JSONPassDoneTag:
      return passDelayFor(object, state);

    // The end of a hand, which waits for the player rather than for a clock
    case Protocol.JSONPlayersPartialResultsTag:
      return HoldForPlayer;

    default:
      // Everything else - the setup, the deck refilling hands, a player leaving, the final
      // results - either draws nothing on its own or is the last thing that happens
      return promptDelayFor(tag, random);
  }
}

// The stripped copies of a request, which every seat but its addressee receives: all they say is
// who the table is waiting for, and they are what makes a bot's turn readable at all
function promptDelayFor(tag, random) {
  switch (tag) {
    case Protocol.JSONBidTag:
    case Protocol.JSONBetTag:
      return Math.round(
        Pace.DeclarationThinkMin + random() * (Pace.DeclarationThinkMax - Pace.DeclarationThinkMin),
      );
    case Protocol.JSONValidCardsTag:
    case Protocol.JSONCallRankTag:
    case Protocol.JSONCallSeedTag:
    case Protocol.JSONCardChosenTag:
      return Pace.OpponentTurnPrompt;
    case Protocol.JSONPassCardsTag:
      return Pace.PassPrompt;
    default:
      return 0;
  }
}

// HeartsGameFragment.passDoneNotification: three cards named by name are worth a long look, a hand
// that passes nothing is worth a glance
function passDelayFor(object, state) {
  const passes = object[Protocol.JSONPassesID] ?? [];
  if (passes.length === 0) return Pace.NothingToPass;
  const mine = passes.some((pass) => pass?.[Protocol.JSONPassToID] === state?.myPosition);
  return mine ? Pace.PassReceived : Pace.PassGivenAway;
}

// The queue itself. It owns nothing but the order and the clock: what a message means is the
// table state's business, and drawing it is the view's
export class Pacer {
  #queue = [];
  #apply;
  #scheduled = null;
  #held = false;
  #timeout;
  #clearTimeout;
  #random;

  // apply(item) -> the table state this message left behind, for delayFor to measure.
  // timers and the dice are injectable so the pacing can be tested without waiting for it
  constructor({
    apply,
    setTimeout: timeout = globalThis.setTimeout.bind(globalThis),
    clearTimeout: cancel = globalThis.clearTimeout.bind(globalThis),
    random = Math.random,
  }) {
    this.#apply = apply;
    this.#timeout = timeout;
    this.#clearTimeout = cancel;
    this.#random = random;
  }

  get pending() {
    return this.#queue.length;
  }

  get holding() {
    return this.#held;
  }

  push(item) {
    this.#queue.push(item);
    this.#pump();
  }

  // The player has read the end of hand panel and wants the next one
  release() {
    if (!this.#held) return;
    this.#held = false;
    this.#pump();
  }

  // A new table, or one being left: whatever was still in the air belonged to the old one
  reset() {
    this.#queue = [];
    this.#held = false;
    if (this.#scheduled !== null) {
      this.#clearTimeout(this.#scheduled);
      this.#scheduled = null;
    }
  }

  #pump() {
    while (this.#scheduled === null && !this.#held) {
      const item = this.#queue.shift();
      if (!item) return;
      const state = this.#apply(item);
      // A reconnection replays a game already watched: it is put back, not played again
      const delay = item.silent ? 0 : delayFor(item, state, this.#random);
      if (delay === HoldForPlayer) {
        this.#held = true;
        return;
      }
      if (delay > 0) {
        this.#scheduled = this.#timeout(() => {
          this.#scheduled = null;
          this.#pump();
        }, delay);
      }
    }
  }
}
